import React, { useCallback, useRef, useState } from 'react';
import { Popover, Tooltip } from 'devextreme-react';

// Animación equivalente a la que usaban los Popover de resultados.
const popoverAnimation = {
  show: { type: 'pop', from: { scale: 0 }, to: { scale: 1 } },
  hide: { type: 'fade', from: 1, to: 0 },
} as const;

type OverlayPosition = 'top' | 'right' | 'bottom' | 'left';

interface LazyTooltipProps {
  /** Contenido del tooltip (se renderiza solo cuando el tooltip se muestra). */
  content: React.ReactNode;
  /** Elemento disparador visible en la celda. */
  children: React.ReactNode;
  position?: OverlayPosition;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
}

/**
 * Envuelve un disparador y monta el widget Tooltip de DevExtreme SOLO tras el
 * primer hover. Hasta entonces no se crea ningún widget ni DOM de overlay, lo
 * que evita instanciar cientos de Tooltips al pintar el grid de resultados.
 *
 * Tras el primer hover el Tooltip queda montado y la visibilidad se controla
 * de forma explícita (mouseenter/mouseleave), conservando el look de DevExtreme.
 */
export function LazyTooltip({ content, children, position = 'right', wrapperClassName, wrapperStyle }: LazyTooltipProps) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleEnter = useCallback(() => {
    setMounted(true);
    setVisible(true);
  }, []);
  const handleLeave = useCallback(() => setVisible(false), []);

  return (
    <div ref={targetRef} className={wrapperClassName} style={wrapperStyle} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {children}
      {mounted && targetRef.current && (
        <Tooltip target={targetRef.current} visible={visible} position={position} hideOnOutsideClick={false}>
          {content}
        </Tooltip>
      )}
    </div>
  );
}

interface LazyPopoverProps {
  /** Contenido del popover. */
  content: React.ReactNode;
  /** Elemento disparador visible en la celda. */
  children: React.ReactNode;
  title?: string;
  position?: OverlayPosition;
  width?: number;
  wrapperClassName?: string;
}

/**
 * Igual que LazyTooltip pero para Popover: el widget se crea en el primer click
 * sobre el disparador. Antes de eso la celda no monta ningún Popover.
 */
export function LazyPopover({ content, children, title, position = 'left', width = 300, wrapperClassName }: LazyPopoverProps) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleClick = useCallback(() => {
    setMounted(true);
    setVisible((v) => !v);
  }, []);
  const handleHiding = useCallback(() => setVisible(false), []);

  return (
    <div ref={targetRef} className={wrapperClassName} onClick={handleClick}>
      {children}
      {mounted && targetRef.current && (
        <Popover
          target={targetRef.current}
          visible={visible}
          onHiding={handleHiding}
          animation={popoverAnimation}
          position={position}
          title={title}
          showTitle={!!title}
          width={width}
          shading={true}
          shadingColor='rgba(0, 0, 0, 0.5)'>
          {content}
        </Popover>
      )}
    </div>
  );
}
